import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  rendicionAnualService,
  SeccionFlujoEfectivo,
  TipoFlujoLinea,
} from '../../../services/api';
import type { AportesTotales, BalanceTotales, FlujoLinea, FlujoResumen, PlanCuenta, SaldoCuenta } from './types';
import BotonRefresh from './BotonRefresh';

const CONCEPTO_AUTO_APORTES = 'Contribuciones y donaciones privadas recibidas';
const PREFIJO_CAJA_Y_BANCOS = '1.1.';

interface DetalleCuenta {
  codigo: string;
  denominacion: string;
  debe: number;
  haber: number;
  neto: number;
  enServidor: boolean;
}

/**
 * Arma el detalle de todas las cuentas 1.1.x del plan, con sus saldos guardados
 * en el servidor (o marcadas como "sin saldo" si no están guardadas).
 */
function detallarCajaYBancos(plan: PlanCuenta[], saldos: SaldoCuenta[]): DetalleCuenta[] {
  const saldosMap = new Map<string, SaldoCuenta>();
  for (const s of saldos) saldosMap.set(s.codigoCuenta, s);

  return plan
    .filter(
      (p) =>
        p.codigo.startsWith(PREFIJO_CAJA_Y_BANCOS) &&
        p.codigo !== PREFIJO_CAJA_Y_BANCOS + '00.00',
    )
    .map((p) => {
      const s = saldosMap.get(p.codigo);
      const debe = s ? Number(s.debe) : 0;
      const haber = s ? Number(s.haber) : 0;
      return {
        codigo: p.codigo,
        denominacion: p.denominacion,
        debe,
        haber,
        neto: debe - haber,
        enServidor: !!s,
      };
    })
    .sort((a, b) => a.codigo.localeCompare(b.codigo));
}

const SECCIONES: { key: SeccionFlujoEfectivo; label: string; hint: string }[] = [
  { key: 'OPERATIVA', label: 'Actividades Operativas', hint: 'Aportes, gastos bancarios, administración corriente' },
  { key: 'INVERSION', label: 'Actividades de Inversión', hint: 'Compra/venta de bienes de uso' },
  { key: 'FINANCIACION', label: 'Actividades de Financiación', hint: 'Préstamos recibidos u otorgados' },
  { key: 'FINANCIERO_TENENCIA', label: 'Resultado Financiero / Tenencia', hint: 'RECPAM, intereses' },
];

interface Props {
  rendicionId: string;
  readonly: boolean;
  snapshotMode?: boolean;
  onBack: () => void;
  onNext: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

function formatImporte(n: number | string): string {
  return Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function parseNum(s: string | number): number {
  if (typeof s === 'number') return s;
  if (!s) return 0;
  const cleaned = s.replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

export default function Paso4Flujo({ rendicionId, readonly, snapshotMode, onBack, onNext, onDirtyChange }: Props) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['rendicion-flujo', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.getFlujo(rendicionId);
      return res.data as { flujo: { efectivoInicio: number | string; efectivoCierre: number | string; lineas: FlujoLinea[] }; resumen: FlujoResumen };
    },
    enabled: !snapshotMode,
    staleTime: snapshotMode ? Infinity : 0,
    refetchOnMount: snapshotMode ? false : 'always',
  });

  // Plan de cuentas + saldos del Paso 3 → para calcular subtotal 1.1.x (caja y bancos)
  const { data: planCuentas } = useQuery({
    queryKey: ['plan-cuentas'],
    queryFn: async () => {
      const res = await rendicionAnualService.getPlanCuentas();
      return res.data as PlanCuenta[];
    },
    staleTime: 60 * 60 * 1000,
  });

  const { data: saldosData } = useQuery({
    queryKey: ['rendicion-saldos', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.listSaldos(rendicionId);
      return res.data as { items: SaldoCuenta[]; totales: BalanceTotales };
    },
    enabled: !snapshotMode,
    staleTime: snapshotMode ? Infinity : 0,
    refetchOnMount: snapshotMode ? false : 'always',
  });

  // Totales del Paso 2 → para sugerir línea de contribuciones privadas en dinero
  const { data: aportesData } = useQuery({
    queryKey: ['rendicion-aportes', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.listAportes(rendicionId);
      return res.data as { items: any[]; totales: AportesTotales };
    },
    enabled: !snapshotMode,
    staleTime: snapshotMode ? Infinity : 0,
    refetchOnMount: snapshotMode ? false : 'always',
  });

  // Detalle de cada cuenta 1.1.x del plan con su saldo del servidor (si está cargado)
  const detalleCajaBancos = useMemo<DetalleCuenta[] | null>(() => {
    if (!planCuentas || !saldosData) return null;
    return detallarCajaYBancos(planCuentas, saldosData.items);
  }, [planCuentas, saldosData]);

  // Efectivo al cierre sugerido = suma de netos (debe − haber) de todas las 1.1.x
  const efectivoCierreDesdeSaldos = useMemo(() => {
    if (!detalleCajaBancos) return null;
    return detalleCajaBancos.reduce((acc, d) => acc + d.neto, 0);
  }, [detalleCajaBancos]);

  const [mostrarDetalleSaldos, setMostrarDetalleSaldos] = useState(false);

  // Total de aportes privados en dinero (TRANSFERENCIA + DEPOSITO, NO especie).
  // Los aportes en especie no entran al flujo de efectivo (RT 8 FACPCE).
  const totalAportesDinero = useMemo(() => {
    if (!aportesData) return 0;
    const { humanasDinero, juridicasDinero } = aportesData.totales;
    return humanasDinero + juridicasDinero;
  }, [aportesData]);

  const [efectivoInicio, setEfectivoInicio] = useState('0');
  const [efectivoCierre, setEfectivoCierre] = useState('0');
  const [lineas, setLineas] = useState<FlujoLinea[]>([]);
  const [expanded, setExpanded] = useState<Record<SeccionFlujoEfectivo, boolean>>({
    OPERATIVA: true,
    INVERSION: false,
    FINANCIACION: false,
    FINANCIERO_TENENCIA: false,
  });

  useEffect(() => {
    if (!data) return;

    const servidorInicio = Number(data.flujo.efectivoInicio ?? 0);
    const servidorCierre = Number(data.flujo.efectivoCierre ?? 0);
    const servidorLineas = (data.flujo.lineas ?? []).map((l, i) => ({
      ...l,
      orden: l.orden ?? i,
      importe: Number(l.importe),
    })) as FlujoLinea[];

    setEfectivoInicio(String(servidorInicio));

    // Pre-carga editable: si el usuario nunca tocó el efectivo al cierre
    // (servidor = 0) y hay saldos en 1.1.x del Paso 3, sugerimos ese valor.
    // Si el usuario ya guardó un valor distinto, respetamos lo suyo.
    if (servidorCierre === 0 && efectivoCierreDesdeSaldos && efectivoCierreDesdeSaldos > 0) {
      setEfectivoCierre(String(efectivoCierreDesdeSaldos));
    } else {
      setEfectivoCierre(String(servidorCierre));
    }

    // Línea auto-sugerida de contribuciones privadas (sección Operativa / Cobro).
    // Solo se inserta si aún no existe ninguna línea con ese concepto.
    const tieneLineaAportes = servidorLineas.some(
      (l) =>
        l.seccion === 'OPERATIVA' &&
        l.tipo === 'COBRO' &&
        l.concepto.trim().toLowerCase() === CONCEPTO_AUTO_APORTES.toLowerCase(),
    );

    if (!tieneLineaAportes && totalAportesDinero > 0 && !readonly) {
      setLineas([
        ...servidorLineas,
        {
          seccion: 'OPERATIVA',
          tipo: 'COBRO',
          concepto: CONCEPTO_AUTO_APORTES,
          importe: totalAportesDinero,
          orden: servidorLineas.length,
        },
      ]);
    } else {
      setLineas(servidorLineas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, efectivoCierreDesdeSaldos, totalAportesDinero]);

  const resumen = useMemo(() => {
    let totalCobros = 0;
    let totalPagos = 0;
    const porSeccion: Record<SeccionFlujoEfectivo, { cobros: number; pagos: number; neto: number }> = {
      OPERATIVA: { cobros: 0, pagos: 0, neto: 0 },
      INVERSION: { cobros: 0, pagos: 0, neto: 0 },
      FINANCIACION: { cobros: 0, pagos: 0, neto: 0 },
      FINANCIERO_TENENCIA: { cobros: 0, pagos: 0, neto: 0 },
    };
    for (const l of lineas) {
      const importe = parseNum(l.importe);
      if (l.tipo === 'COBRO') {
        porSeccion[l.seccion].cobros += importe;
        totalCobros += importe;
      } else {
        porSeccion[l.seccion].pagos += importe;
        totalPagos += importe;
      }
      porSeccion[l.seccion].neto = porSeccion[l.seccion].cobros - porSeccion[l.seccion].pagos;
    }
    const inicio = parseNum(efectivoInicio);
    const cierre = parseNum(efectivoCierre);
    const variacionNeta = Math.round((cierre - inicio) * 100) / 100;
    const flujoNeto = Math.round((totalCobros - totalPagos) * 100) / 100;
    return {
      totalCobros,
      totalPagos,
      flujoNeto,
      variacionNeta,
      porSeccion,
      coincide: Math.abs(variacionNeta - flujoNeto) < 0.01,
    };
  }, [lineas, efectivoInicio, efectivoCierre]);

  function addLinea(seccion: SeccionFlujoEfectivo) {
    setLineas((prev) => [
      ...prev,
      { seccion, tipo: 'COBRO', concepto: '', importe: 0, orden: prev.length },
    ]);
    setExpanded((p) => ({ ...p, [seccion]: true }));
  }

  function updateLinea(idx: number, patch: Partial<FlujoLinea>) {
    setLineas((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  function removeLinea(idx: number) {
    setLineas((prev) => prev.filter((_, i) => i !== idx));
  }

  // Dirty check: hay cambios sin guardar si lo local difiere de lo último que vino del server.
  const isDirty = useMemo(() => {
    if (!data) return false;
    const srvInicio = Number(data.flujo.efectivoInicio ?? 0);
    const srvCierre = Number(data.flujo.efectivoCierre ?? 0);
    if (Math.abs(parseNum(efectivoInicio) - srvInicio) > 0.001) return true;
    if (Math.abs(parseNum(efectivoCierre) - srvCierre) > 0.001) return true;

    // Líneas locales "efectivas" (con concepto e importe > 0), ordenadas por sección+orden.
    const localVals = lineas
      .filter((l) => l.concepto.trim() && parseNum(l.importe) > 0)
      .map((l) => `${l.seccion}|${l.tipo}|${l.concepto.trim()}|${parseNum(l.importe)}`)
      .sort();
    const srvVals = (data.flujo.lineas ?? [])
      .map((l) => `${l.seccion}|${l.tipo}|${l.concepto.trim()}|${Number(l.importe)}`)
      .sort();
    if (localVals.length !== srvVals.length) return true;
    for (let i = 0; i < localVals.length; i++) {
      if (localVals[i] !== srvVals[i]) return true;
    }
    return false;
  }, [data, efectivoInicio, efectivoCierre, lineas]);

  // Validación cruzada extra: efectivo al cierre local vs saldo 1.1.x del Paso 3.
  const coincideConSaldo = useMemo(() => {
    if (efectivoCierreDesdeSaldos === null) return true; // sin datos, no validamos
    return Math.abs(parseNum(efectivoCierre) - efectivoCierreDesdeSaldos) < 0.01;
  }, [efectivoCierre, efectivoCierreDesdeSaldos]);

  // Informo al shell del estado dirty (para que el stepper pregunte antes de cambiar)
  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return rendicionAnualService.saveFlujo(rendicionId, {
        efectivoInicio: parseNum(efectivoInicio),
        efectivoCierre: parseNum(efectivoCierre),
        lineas: lineas
          .filter((l) => l.concepto.trim() && parseNum(l.importe) > 0)
          .map((l, i) => ({
            seccion: l.seccion,
            tipo: l.tipo as TipoFlujoLinea,
            concepto: l.concepto.trim(),
            importe: parseNum(l.importe),
            orden: i,
          })),
      });
    },
    onSuccess: () => {
      toast.success('Flujo de efectivo guardado');
      queryClient.invalidateQueries({ queryKey: ['rendicion-flujo', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-validaciones', rendicionId] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo guardar'),
  });

  if (isLoading) {
    return <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">Cargando...</div>;
  }

  function refrescarPaso() {
    queryClient.invalidateQueries({ queryKey: ['rendicion-flujo', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-saldos', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-aportes', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['plan-cuentas'] });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end -mb-2">
        <BotonRefresh onClick={refrescarPaso} title="Actualizar paso" />
      </div>
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
          Estado de Flujo de Efectivo (Método Directo)
        </h2>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm text-blue-900 mb-4 space-y-1">
          <p>
            Se carga manualmente porque la ley exige clasificar los movimientos por actividad
            (<strong>Operativa / Inversión / Financiación / Financiero-Tenencia</strong>, RT 8 FACPCE).
          </p>
          <p>
            El <strong>Efectivo al cierre</strong> debe coincidir con el subtotal del rubro{' '}
            <code className="bg-blue-100 px-1 rounded">1.1.00.00 Caja y Bancos Corrientes</code> del Paso 3.
            El <strong>flujo neto (cobros − pagos)</strong> debe coincidir con la variación{' '}
            (cierre − inicio). Si alguna no coincide, se marca en rojo.
          </p>
          <p className="text-xs text-blue-800">
            Nota: los <strong>aportes en especie</strong> no entran al Flujo de Efectivo porque no son
            movimientos monetarios (RT 8). Se declaran como Nota a los Estados Contables.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Efectivo al inicio del ejercicio
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={efectivoInicio}
              onChange={(e) => setEfectivoInicio(e.target.value)}
              readOnly={readonly}
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-right ${
                readonly ? 'bg-gray-100' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Efectivo al cierre del ejercicio
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={efectivoCierre}
              onChange={(e) => setEfectivoCierre(e.target.value)}
              readOnly={readonly}
              className={`w-full border rounded-lg px-3 py-2 text-sm font-mono text-right ${
                readonly ? 'bg-gray-100 border-gray-300' : coincideConSaldo ? 'border-gray-300' : 'border-red-400 bg-red-50'
              }`}
            />
            {efectivoCierreDesdeSaldos !== null && (
              <>
                <p className={`mt-1 text-xs ${coincideConSaldo ? 'text-gray-500' : 'text-red-700'}`}>
                  {coincideConSaldo ? (
                    <>
                      ✓ Coincide con saldo 1.1.x del Paso 3 ($ {formatImporte(efectivoCierreDesdeSaldos)})
                    </>
                  ) : (
                    <>
                      ⚠ No coincide con saldo <strong>1.1.00.00 Caja y Bancos</strong> del Paso 3
                      ($ {formatImporte(efectivoCierreDesdeSaldos)}).
                      {!readonly && (
                        <button
                          type="button"
                          onClick={() => setEfectivoCierre(String(efectivoCierreDesdeSaldos))}
                          className="ml-1 underline font-semibold hover:text-red-900"
                        >
                          Usar valor del Paso 3
                        </button>
                      )}
                    </>
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => setMostrarDetalleSaldos((v) => !v)}
                  className="mt-1 text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {mostrarDetalleSaldos ? 'Ocultar' : 'Ver'} detalle cuentas 1.1.x
                </button>
                {mostrarDetalleSaldos && detalleCajaBancos && (
                  <div className="mt-2 border border-gray-200 rounded-lg bg-gray-50 p-2 text-xs">
                    <table className="min-w-full">
                      <thead>
                        <tr className="text-gray-600 uppercase">
                          <th className="text-left pb-1">Cuenta</th>
                          <th className="text-right pb-1 w-20">Debe</th>
                          <th className="text-right pb-1 w-20">Haber</th>
                          <th className="text-right pb-1 w-20">Neto</th>
                          <th className="text-center pb-1 w-16">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalleCajaBancos.map((d) => (
                          <tr key={d.codigo} className="border-t border-gray-200">
                            <td className="py-0.5 font-mono text-gray-700">
                              {d.codigo} <span className="text-gray-500">{d.denominacion}</span>
                            </td>
                            <td className="py-0.5 text-right font-mono">{formatImporte(d.debe)}</td>
                            <td className="py-0.5 text-right font-mono">{formatImporte(d.haber)}</td>
                            <td className={`py-0.5 text-right font-mono font-semibold ${d.neto > 0 ? 'text-green-700' : d.neto < 0 ? 'text-red-700' : 'text-gray-500'}`}>
                              {formatImporte(d.neto)}
                            </td>
                            <td className="py-0.5 text-center">
                              {d.enServidor ? (
                                <span className="text-green-600" title="Guardado en BD">●</span>
                              ) : (
                                <span className="text-gray-400" title="Sin saldo cargado">○</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-400 font-bold">
                          <td className="pt-1">Total rubro 1.1.00.00</td>
                          <td colSpan={2}></td>
                          <td className="pt-1 text-right font-mono">
                            {formatImporte(efectivoCierreDesdeSaldos)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                    <p className="mt-2 text-gray-500 italic">
                      ● = saldo guardado en servidor · ○ = sin saldo cargado todavía. Si
                      falta una cuenta con saldo, volvé al Paso 3 y asegurate de haber
                      apretado <strong>Guardar saldos</strong>.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Variación neta
            </label>
            <div
              className={`w-full border rounded-lg px-3 py-2 text-sm font-mono text-right font-bold ${
                resumen.coincide ? 'bg-green-50 border-green-300 text-green-800' : 'bg-red-50 border-red-300 text-red-800'
              }`}
            >
              $ {formatImporte(resumen.variacionNeta)}
            </div>
          </div>
        </div>

        {SECCIONES.map((s) => {
          const lineasSeccion = lineas
            .map((l, i) => ({ l, i }))
            .filter(({ l }) => l.seccion === s.key);
          const totales = resumen.porSeccion[s.key];
          return (
            <div key={s.key} className="border border-gray-200 rounded-lg mb-3">
              <button
                type="button"
                onClick={() => setExpanded((p) => ({ ...p, [s.key]: !p[s.key] }))}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
              >
                <div>
                  <div className="font-semibold text-sm text-gray-900">{s.label}</div>
                  <div className="text-xs text-gray-600">{s.hint}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-gray-700">
                    Neto: $ {formatImporte(totales.neto)}
                  </span>
                  <span className="text-gray-400">{expanded[s.key] ? '▼' : '▶'}</span>
                </div>
              </button>
              {expanded[s.key] && (
                <div className="p-4 space-y-2">
                  {lineasSeccion.length === 0 ? (
                    <p className="text-xs text-gray-500 italic">Sin movimientos.</p>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-xs text-gray-500 uppercase">
                          <th className="text-left pb-2">Concepto</th>
                          <th className="text-right pb-2 w-32">Importe</th>
                          <th className="text-center pb-2 w-28">Tipo</th>
                          {!readonly && <th className="w-8"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {lineasSeccion.map(({ l, i }) => (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="py-1.5">
                              <input
                                type="text"
                                value={l.concepto}
                                readOnly={readonly}
                                onChange={(e) => updateLinea(i, { concepto: e.target.value })}
                                placeholder="Ej: Contribuciones privadas"
                                className={`w-full border border-gray-300 rounded px-2 py-1 text-xs ${
                                  readonly ? 'bg-gray-100' : ''
                                }`}
                              />
                            </td>
                            <td className="py-1.5">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={String(l.importe)}
                                readOnly={readonly}
                                onChange={(e) => updateLinea(i, { importe: e.target.value })}
                                className={`w-28 border border-gray-300 rounded px-2 py-1 text-xs font-mono text-right ${
                                  readonly ? 'bg-gray-100' : ''
                                }`}
                              />
                            </td>
                            <td className="py-1.5 text-center">
                              <select
                                value={l.tipo}
                                disabled={readonly}
                                onChange={(e) =>
                                  updateLinea(i, { tipo: e.target.value as TipoFlujoLinea })
                                }
                                className={`border border-gray-300 rounded px-2 py-1 text-xs ${
                                  readonly ? 'bg-gray-100' : ''
                                }`}
                              >
                                <option value="COBRO">Cobro</option>
                                <option value="PAGO">Pago</option>
                              </select>
                            </td>
                            {!readonly && (
                              <td className="py-1.5 text-right">
                                <button
                                  type="button"
                                  onClick={() => removeLinea(i)}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  ✕
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {!readonly && (
                    <button
                      type="button"
                      onClick={() => addLinea(s.key)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      + Agregar línea
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 border-t border-gray-200 pt-4">
          <TotalBox label="Total cobros" value={resumen.totalCobros} tone="success" />
          <TotalBox label="Total pagos" value={resumen.totalPagos} tone="danger" />
          <TotalBox
            label="Flujo neto"
            value={resumen.flujoNeto}
            tone={resumen.coincide ? 'success' : 'danger'}
          />
        </div>

        {!resumen.coincide && (
          <div className="mt-3 bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-900">
            La variación neta (<strong>$ {formatImporte(resumen.variacionNeta)}</strong>) no coincide con el
            flujo neto (<strong>$ {formatImporte(resumen.flujoNeto)}</strong>). Revise los saldos inicio/cierre
            y las líneas cargadas.
          </div>
        )}

        {/* Estado del flujo — debajo del resumen, sin botón guardar (se unificó con "Continuar") */}
        <div className="mt-4 border-t border-gray-200 pt-4 flex items-center gap-2 flex-wrap">
          {resumen.coincide ? (
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-green-100 text-green-800">
              ✓ FLUJO COINCIDE
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-red-100 text-red-800">
              ✗ NO COINCIDE · dif. $ {formatImporte(Math.abs(resumen.variacionNeta - resumen.flujoNeto))}
            </span>
          )}
          {isDirty && (
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
              ● Cambios sin guardar
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-5 py-2 rounded-lg"
        >
          ← Anterior
        </button>
        <button
          type="button"
          disabled={saveMutation.isPending}
          onClick={async () => {
            if (isDirty && !readonly) {
              try {
                await saveMutation.mutateAsync();
              } catch {
                return;
              }
            }
            onNext();
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50"
        >
          {saveMutation.isPending
            ? 'Guardando...'
            : isDirty && !readonly
            ? 'Guardar y continuar →'
            : 'Continuar →'}
        </button>
      </div>
    </div>
  );
}

function TotalBox({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'success' | 'danger';
}) {
  const toneClass =
    tone === 'success' ? 'text-green-700' : tone === 'danger' ? 'text-red-700' : 'text-gray-900';
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
      <div className="text-xs font-semibold uppercase text-gray-600 mb-1">{label}</div>
      <div className={`text-lg font-bold font-mono ${toneClass}`}>$ {formatImporte(value)}</div>
    </div>
  );
}
